function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import React, { useEffect, useMemo, useState, useRef } from 'react';
import cn from 'classnames';
import { Popper, ClickAwayListener } from '@material-ui/core';
import { PopoverArrowIcon } from "../PopoverArrowIcon";
import styles from "./PopoverOnHover.module.scss";
import zIndex from "../../foundations/zIndex.module.scss";
PopoverOnHover.defaultProps = {
  placement: 'top',
  timeout: 200,
  openStrategy: 'auto',
  removePadding: false
};
/**
 * Opens a popover that stays open as long as you keep the mouse hovered on the content.
 */

export function PopoverOnHover(props) {
  const {
    trigger,
    content,
    timeout,
    id,
    isOpen,
    placement = 'top',
    onOpen,
    onClose,
    openStrategy,
    showArrow,
    removePadding,
    container
  } = props;
  const [anchorEl, setAnchorEl] = useState(null);
  const timeoutIdRef = useRef(undefined);
  const [open, setOpen] = useState(isOpen || false);
  useEffect(() => {
    return () => {
      clearTimeout(timeoutIdRef.current);
    };
  }, []);
  useEffect(() => {
    setOpen(isOpen || false);
  }, [isOpen]);
  const popperId = useMemo(() => {
    return id || Math.random().toString();
  }, [id]);

  const schedulePopoverHiding = () => {
    timeoutIdRef.current = setTimeout(() => {
      setOpen(false);
      onClose && onClose();
    }, timeout);
  };

  const triggerPropsCommon = {
    ref: el => setAnchorEl(el),
    ['aria-owns']: open ? popperId : undefined,
    ['aria-haspopup']: 'true',
    ['data-popover-is-open']: open ? 'true' : 'false'
  };

  const triggerPropsHoverStrategy = _objectSpread({
    onMouseEnter: () => {
      setOpen(true);
      clearTimeout(timeoutIdRef.current);
      onOpen && onOpen();
    },
    onMouseLeave: schedulePopoverHiding
  }, triggerPropsCommon);

  const triggerPropsClickStrategy = _objectSpread({
    onClick: () => {
      setOpen(!open);

      if (open) {
        onClose && onClose();
      } else {
        onOpen && onOpen();
      }
    }
  }, triggerPropsCommon);

  const targetContainerPropsHoverStrategy = {
    onClick: e => e.stopPropagation(),
    onMouseEnter: () => {
      clearTimeout(timeoutIdRef.current);
    },
    onMouseLeave: schedulePopoverHiding
  };
  const targetContainerPropsClickStrategy = {
    onClick: event => event.stopPropagation()
  };
  const targetContainerPropsAutoStrategy = {
    onClick: event => event.stopPropagation()
  };
  let triggerProps = {},
      targetContainerProps = {};

  switch (openStrategy) {
    case 'auto':
      if (isOpen === undefined) {
        // if no param is passed => change to hover strategy
        triggerProps = triggerPropsHoverStrategy;
        targetContainerProps = targetContainerPropsHoverStrategy;
      } else {
        triggerProps = triggerPropsCommon;
        targetContainerProps = targetContainerPropsAutoStrategy;
      }

      break;

    case 'hover':
      triggerProps = triggerPropsHoverStrategy;
      targetContainerProps = targetContainerPropsHoverStrategy;
      break;

    case 'click':
      triggerProps = triggerPropsClickStrategy;
      targetContainerProps = targetContainerPropsClickStrategy;
      break;
  }

  const contentProps = {
    closePopover: () => setOpen(false)
  };
  const placementClass = useMemo(() => {
    if (placement.startsWith('top')) {
      return 'top';
    } else if (placement.startsWith('bottom')) {
      return 'bottom';
    } else if (placement.startsWith('left')) {
      return 'left';
    } else {
      return 'right';
    }
  }, [placement]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, trigger(triggerProps), /*#__PURE__*/React.createElement("div", targetContainerProps, anchorEl && /*#__PURE__*/React.createElement(Popper, {
    style: {
      zIndex: zIndex.popover
    },
    id: popperId,
    open: open,
    anchorEl: anchorEl,
    placement: placement,
    className: styles[placementClass],
    container: container,
    modifiers: {
      offset: {
        // Distance from the trigger element.
        // See: https://popper.js.org/docs/v1/#modifiers..offset
        offset: '0, 8'
      }
    }
  }, showArrow && /*#__PURE__*/React.createElement("span", {
    className: cn(styles.arrow),
    "x-arrow": "true"
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.arrowShadow
  }), /*#__PURE__*/React.createElement(PopoverArrowIcon, {
    className: styles.arrowCover
  })), /*#__PURE__*/React.createElement(ClickAwayListener, {
    onClickAway: () => openStrategy !== 'auto' && setOpen(false)
  }, /*#__PURE__*/React.createElement("div", {
    className: cn(styles.popoverContent, {
      [styles.withArrow]: showArrow,
      [styles.builtInPadding]: !removePadding
    })
  }, typeof content === 'function' ? content(contentProps) : content)))));
}